import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
  input,
  computed,
  HostBinding,
} from '@angular/core';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import {
  Product,
  ProductOptionItem,
  PurchaseOptions,
  PurchaseRestrictable,
  hasDeclaredPurchaseRestriction,
} from '@pedix-workspace/utils';
import { ToastrService } from 'ngx-toastr';

import { Params, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import {
  IconClockComponent,
  IconCrossArrowsComponent,
  IconEditComponent,
  IconEyeSlashComponent,
  IconTrashCanComponent,
} from '@pedix-workspace/angular-ui-icons';
import { ImageComponent, ImageSize } from '@pedix-workspace/angular-ui-image';
import { TagComponent } from '@pedix-workspace/angular-ui-alert';
import {
  ProductListItemPriceComponent,
  ProductListItemSize,
} from '../product-list-item-price/product-list-item-price.component';
import clsx from 'clsx';
import { ProductTagComponent } from '../product-tag/product-tag.component';
import { ProductRestrictionTagsComponent } from '../product-restriction-tags/product-restriction-tags.component';
import {
  computeHeadlineDiscount,
  extractHeadlineVariants,
  PromotionInput,
} from '@pedix-workspace/shared-promotions';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-product-list-item',
  templateUrl: './product-list-item.component.html',
  styleUrls: ['./product-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    NgClass,
    RouterLink,
    IconEyeSlashComponent,
    IconClockComponent,
    IconEditComponent,
    IconTrashCanComponent,
    IconCrossArrowsComponent,
    ImageComponent,
    ProductListItemPriceComponent,
    ProductTagComponent,
    ProductRestrictionTagsComponent,
    TagComponent,
  ],
})
export class ProductListItemComponent {
  private toastrService = inject(ToastrService);
  private t = inject(TranslocoService);

  @Input() id: string;
  @Input() name: string;
  @Input() image: string;
  @Input() price: number;
  @Input() priceDiscount: number;
  @Input() sku?: string;
  @Input() hidden: boolean;
  @Input() tag: { tag: string; color: string };
  @Input() presentationItems?: ProductOptionItem[];
  @Input() isAvailable = true;
  @Input() isUpdatingImages?: boolean;
  @Input() listIndex: number;
  @Input() sortEnabled: boolean;
  @Input() isAdminMode: boolean;
  @Input() currencySymbol: string;
  @Input() detailRoute: string | string[] = [];
  @Input() detailRouteQueryParams: Params | null = null;
  @Input() displaySku = false;
  @Input() lazyLoadImageMinIndex = 9;
  @Input() promotions: PromotionInput[] = [];
  @Input() imageSize: ImageSize = 100;
  @Input() promotion?: PromotionInput;

  readonly withoutStock = input.required<boolean>();
  readonly isExample = input(false);
  readonly size = input<ProductListItemSize>('md');
  readonly purchaseRestrictions = input<PurchaseRestrictable | null>(null);
  readonly purchaseOptions = input<PurchaseOptions | null>(null);
  readonly hasPurchaseRestrictions = computed(() => {
    const restrictions = this.purchaseRestrictions();

    return !!restrictions && hasDeclaredPurchaseRestriction(restrictions);
  });

  @Output() clickEdit = new EventEmitter<Pick<Product, 'id'>>();
  @Output() clickDelete = new EventEmitter<Pick<Product, 'id' | 'name'>>();

  get entity() {
    return { id: this.id, name: this.name };
  }

  get presentationMinimumPrice(): number {
    return (
      this.presentationItems?.reduce((minimumPrice, presentation) => {
        const currentPrice = presentation.priceDiscount || presentation.price;

        return currentPrice < minimumPrice ? currentPrice : minimumPrice;
      }, Number.MAX_VALUE) || 0
    );
  }

  get headlineDiscount(): number {
    const variants = extractHeadlineVariants({
      price: this.price,
      priceDiscount: this.priceDiscount,
      presentationItems: this.presentationItems,
    });
    return computeHeadlineDiscount(variants, this.promotions);
  }

  @HostBinding('class')
  get classNames() {
    return clsx({
      'with-discount': this.headlineDiscount > 0,
      'with-sku': this.displaySku && this.sku,
      'admin-mode': this.isAdminMode,
      'product-inactive': this.withoutStock() || this.hidden,
      'size-sm': this.size() === 'sm',
    });
  }

  onClickEdit($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.isUpdatingImages) {
      this.toastrService.info(this.t.translate('product.waitingBackgroundTask'));

      return;
    }

    this.clickEdit.emit(this.entity);
  }

  onClickDelete($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.clickDelete.emit(this.entity);
  }
}
